import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, fmtINR, today } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { inDateRange } from "@/lib/csv";
import { PageHeader, Card, Btn, Field, Input, Select, Combobox, Empty } from "@/components/UI";
import { toast } from "sonner";

const empty = { date: today(), product_id: "", pcs: "", mode: "inhouse", party_id: "", worker_id: "", rate: "", notes: "", status: "received" };

export default function StitchingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [products, setProducts] = useState([]);
  const [parties, setParties] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [f, setF] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [modeFilter, setModeFilter] = useState("");
  const [pendingOnly, setPendingOnly] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = () => api.get("/stitching").then((r) => setList(r.data));
  useEffect(() => {
    load();
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/parties").then((r) => setParties(r.data.filter((p) => p.type === "stitcher")));
    api.get("/workers").then((r) => setWorkers(r.data.filter((w) => w.active !== false)));
  }, []);

  const startEdit = (s) => {
    setEditing(s.id);
    setF({
      date: s.date, product_id: s.product_id, pcs: s.pcs, mode: s.mode,
      party_id: s.party_id || "", worker_id: s.worker_id || "", rate: s.rate || 0, notes: s.notes || "",
      status: s.status || "received",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => { setEditing(null); setF(empty); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...f, pcs: parseInt(f.pcs), rate: Number(f.rate || 0),
        party_id: f.mode === "outsourced" ? f.party_id : null,
        worker_id: f.worker_id || null,
      };
      if (editing) {
        await api.put(`/stitching/${editing}`, payload);
        toast.success("Stitching updated · ledger adjusted");
      } else {
        await api.post("/stitching", payload);
        toast.success("Stitching saved");
      }
      cancelEdit();
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete? Linked ledger bill will also be removed.")) return;
    await api.delete(`/stitching/${id}`);
    load();
  };

  const markReceived = async (id) => {
    if (!window.confirm("Mark this stitching batch as received? This will post the vendor bill to the ledger.")) return;
    try {
      await api.put(`/stitching/${id}/status`, { status: "received" });
      toast.success("Marked as received · ledger updated");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return list.filter((s) => {
      if (pendingOnly && !(s.mode === "outsourced" && s.status === "in_process")) return false;
      if (!inDateRange(s.date, from, to)) return false;
      if (modeFilter && s.mode !== modeFilter) return false;
      if (needle) {
        const hay = `${s.product_label} ${s.party_name} ${s.worker_name || ""} ${s.notes || ""}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [list, q, modeFilter, from, to, pendingOnly]);

  const pendingCount = useMemo(
    () => list.filter((s) => s.mode === "outsourced" && s.status === "in_process").length,
    [list]
  );

  return (
    <div>
      <PageHeader label="Stage 06" title="Stitching" subtitle="Inhouse Jobs · use /dcs → Stitching tab for Outsourced DCs"
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setPendingOnly((v) => !v)}
              data-testid="stitching-pending-toggle"
              className={`h-9 px-3 font-mono text-[11px] tracking-[0.15em] uppercase font-bold ${pendingOnly ? "bg-destructive text-white" : "bg-white border border-border"}`}
            >
              {pendingOnly ? "✓ Pending Only" : `Pending Only · ${pendingCount}`}
            </button>
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search product/stitcher/worker…" className="h-9 px-3 bg-white border border-border font-mono text-xs w-56" data-testid="stitching-search" />
            <Select value={modeFilter} onChange={(e) => setModeFilter(e.target.value)} className="w-32" data-testid="stitching-mode-filter">
              <option value="">All modes</option>
              <option value="inhouse">Inhouse</option>
              <option value="outsourced">Outsourced</option>
            </Select>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 px-2 bg-white border border-border font-mono text-xs" data-testid="stitching-from" />
            <span className="text-xs text-muted-foreground">→</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 px-2 bg-white border border-border font-mono text-xs" data-testid="stitching-to" />
          </div>
        }
      />
      <div className="px-4 md:px-8 pt-4 flex gap-2">
        <button data-testid="stitching-tab-inhouse" className="h-9 px-4 font-mono text-[11px] tracking-[0.15em] uppercase font-bold bg-foreground text-background">Inhouse Jobs</button>
        <button data-testid="stitching-tab-outsourced" onClick={() => navigate("/dcs?stage=stitching")} className="h-9 px-4 font-mono text-[11px] tracking-[0.15em] uppercase font-bold bg-white border border-border hover:bg-secondary">Outsourced DCs ›</button>
      </div>
      <div className="p-4 md:p-8 grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4">
        <Card title={editing ? "Edit Stitching Entry" : "New Stitching Entry"} right={editing && (
          <button type="button" onClick={cancelEdit} className="label-caps hover:text-destructive">Cancel</button>
        )}>
          <form onSubmit={submit} className="space-y-3" data-testid="stitching-form">
            <Field label="Date"><Input type="date" required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
            <Field label="Product">
              <Combobox
                testid="stitching-product"
                required
                value={f.product_id}
                onChange={(v) => setF({ ...f, product_id: v })}
                placeholder="Type to search product / SKU / size…"
                options={products.map((p) => ({
                  value: p.id,
                  label: `${p.style_name} / ${p.sku} / ${p.size} / ${p.color}`,
                  hint: p.pieces_per_box ? `${p.pieces_per_box} pcs / box` : "",
                }))}
              />
            </Field>
            <Field label="Pieces"><Input data-testid="stitching-pcs" type="number" required value={f.pcs} onChange={(e) => setF({ ...f, pcs: e.target.value })} /></Field>
            <Field label="Worker (inhouse) — optional">
              <Select data-testid="stitching-worker" value={f.worker_id} onChange={(e) => setF({ ...f, worker_id: e.target.value })}>
                <option value="">— Unassigned —</option>
                {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </Select>
            </Field>
            <Field label="Mode">
              <div className="grid grid-cols-2 gap-2">
                {["inhouse", "outsourced"].map((m) => (
                  <button type="button" key={m} onClick={() => setF({ ...f, mode: m })}
                    className={`h-9 border font-mono text-xs uppercase tracking-[0.15em] ${f.mode === m ? "bg-foreground text-background border-foreground" : "bg-white border-border"}`}
                    data-testid={`stitching-mode-${m}`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </Field>
            {f.mode === "outsourced" && (
              <>
                <Field label="Stitcher">
                  <Combobox
                    testid="stitching-party"
                    required
                    value={f.party_id}
                    onChange={(v) => setF({ ...f, party_id: v })}
                    placeholder="Type to search stitcher…"
                    options={parties.map((p) => ({ value: p.id, label: p.name, hint: p.phone || "" }))}
                  />
                </Field>
                <Field label="Rate (₹ / Pc)"><Input type="number" step="0.01" value={f.rate} onChange={(e) => setF({ ...f, rate: e.target.value })} /></Field>
                <Field label="Status">
                  <Select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
                    <option value="in_process">In-Process (at stitcher)</option>
                    <option value="received">Received</option>
                  </Select>
                </Field>
                <div className="border border-border p-2 bg-secondary tabular text-sm">
                  Cost: <b>{fmtINR((Number(f.pcs) || 0) * (Number(f.rate) || 0))}</b>
                </div>
              </>
            )}
            <Field label="Notes"><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
            <Btn data-testid="stitching-submit">{editing ? "Save Changes" : "Save Stitching"}</Btn>
          </form>
        </Card>
        <Card title={`Stitching History · ${filtered.length}`} className="lg:col-span-2">
          <div className="table-scroll">
            <table className="table-dense w-full">
              <thead><tr><th>Date</th><th>Product</th><th>Mode</th><th>Party/Worker</th><th className="text-right">Pcs</th><th className="text-right">Rate</th><th className="text-right">Cost</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {filtered.map((s) => (
                  <tr key={s.id} className={editing === s.id ? "bg-secondary" : ""}>
                    <td className="font-mono">{s.date}</td>
                    <td>{s.product_label}</td>
                    <td><span className="label-caps">{s.mode}</span></td>
                    <td>
                      {s.party_name || <span className="text-muted-foreground">—</span>}
                      {s.worker_name && <div className="text-xs text-muted-foreground">by {s.worker_name}</div>}
                    </td>
                    <td className="tabular text-right">{s.pcs}</td>
                    <td className="tabular text-right">{s.rate ? `₹${s.rate}` : "—"}</td>
                    <td className="tabular text-right">{s.cost ? fmtINR(s.cost) : "—"}</td>
                    <td>
                      <span className={`label-caps ${s.status === "in_process" ? "text-destructive" : "text-green-700"}`}>
                        {(s.status || "received").replace("_", " ")}
                      </span>
                    </td>
                    <td className="text-right whitespace-nowrap">
                      {s.status === "in_process" && s.mode === "outsourced" && (
                        <button onClick={() => markReceived(s.id)} className="label-caps hover:text-green-700 mr-3" data-testid={`stitching-receive-${s.id}`}>✓ Receive</button>
                      )}
                      {isAdmin && (
                        <>
                          <button onClick={() => startEdit(s)} className="label-caps hover:text-foreground mr-3" data-testid={`stitching-edit-${s.id}`}>Edit</button>
                          <button onClick={() => del(s.id)} className="label-caps hover:text-destructive">Del</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={9}><Empty text="No stitching entries match" /></td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
